/**
 * MachineScheduleFilters.tsx
 * Phase 2, Item 10 — Filter dropdowns for the Machine Schedule view.
 *
 * Three multi-select dropdowns:
 *   1. Lab Section  — from the LabSection union type
 *   2. Machine      — machine code + name; filtered by selected lab sections
 *   3. Work Type    — Quote | WorkOrder | Manual (MachineBookingSource)
 *
 * Plus a "Clear Filters" button.
 *
 * FEAT-2026-0007 6E.1 — removed Quote / Work Order single-select dropdowns
 *                       (BUG-2026-0074) and the bookingsInWindow prop. The
 *                       entry point for quote/WO planning is Scenario Planning.
 *
 * This component is presentation-only. Filter state is managed by
 * useMachineScheduleFilters (URL-sticky hook) and passed in as props.
 */

import React from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Button,
  Typography,
  Chip,
  OutlinedInput,
} from '@mui/material';
import { FilterAlt, FilterAltOff } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import type { SelectChangeEvent } from '@mui/material';
import type {
  LabSection,
  MachineBookingCalendar,
  MachineBookingSource,
} from '../../../types/schedule';
import type { MachineScheduleFilterState } from './useMachineScheduleFilters';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const LAB_SECTION_OPTIONS: LabSection[] = [
  'Rock',
  'Triaxial',
  'DSS-DRS',
  'Oedometer',
  'Ring Shear',
  'Soil',
  // BUG-2026-0207: Index machines (incl. the virtual INDEX machine) carry
  // LabSection 'Index'; without this entry they were unreachable by the filter.
  'Index',
  'Other',
];

export const WORK_TYPE_OPTIONS: MachineBookingSource[] = [
  'Quote',
  'WorkOrder',
  'Manual',
];

export const WORK_TYPE_LABELS: Record<MachineBookingSource, string> = {
  Quote: 'Quote (Tentative)',
  WorkOrder: 'Work Order',
  Manual: 'Manual Event',
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface MachineSummaryForFilter {
  machineKey: string;
  code: string;
  name: string;
  labSection: LabSection | null;
}

/**
 * Narrow row shape re-exported for Master Gantt's quote/WO filter dropdown
 * derivation (masterGanttHelpers.ts). These three fields are the only ones
 * the dropdown-option derivation reads. Keeping the type here avoids
 * duplicating the Pick<> in two places.
 */
export type FilterBookingRow = Pick<
  MachineBookingCalendar,
  'TaskQuoteKey' | 'TaskWorkOrderKey' | 'TaskName'
>;

interface MachineScheduleFiltersProps {
  /** Current filter state (from useMachineScheduleFilters hook). */
  filterState: MachineScheduleFilterState;
  /** All active machines — used to build the Machine dropdown options. */
  machines: MachineSummaryForFilter[];
  /** Whether any filter is active (used to style the clear button). */
  hasActiveFilters: boolean;
  /** Called when filter state changes. Patch only the changed fields. */
  onFilterChange: (patch: Partial<MachineScheduleFilterState>) => void;
  /** Called when user clicks "Clear Filters". */
  onClearFilters: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const MULTI_SELECT_PROPS = {
  input: <OutlinedInput size="small" />,
  renderValue: (selected: string[]) =>
    selected.length === 0 ? '' : selected.join(', '),
  MenuProps: {
    PaperProps: {
      style: { maxHeight: 280 },
    },
  },
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const MachineScheduleFilters: React.FC<MachineScheduleFiltersProps> = ({
  filterState,
  machines,
  hasActiveFilters,
  onFilterChange,
  onClearFilters,
}) => {
  const theme = useTheme();

  // ── Lab Section ──────────────────────────────────────────────────────────

  const handleLabSectionChange = (e: SelectChangeEvent<string[]>) => {
    const val = e.target.value;
    const next = typeof val === 'string' ? val.split(',') : val;
    onFilterChange({ labSections: next as LabSection[] });
  };

  // ── Machine ───────────────────────────────────────────────────────────────
  // Filter machine options by selected lab sections (if any).
  const machineOptions = filterState.labSections.length > 0
    ? machines.filter((m) => {
        const section = m.labSection ?? 'Other';
        return filterState.labSections.includes(section as LabSection);
      })
    : machines;

  const handleMachineChange = (e: SelectChangeEvent<string[]>) => {
    const val = e.target.value;
    const next = typeof val === 'string' ? val.split(',') : val;
    // Drop any selected machines that are no longer in the options (lab section changed).
    const validCodes = new Set(machineOptions.map((m) => m.code));
    onFilterChange({ machineCodes: next.filter((c) => validCodes.has(c)) });
  };

  // ── Work Type ─────────────────────────────────────────────────────────────

  const handleWorkTypeChange = (e: SelectChangeEvent<string[]>) => {
    const val = e.target.value;
    const next = typeof val === 'string' ? val.split(',') : val;
    onFilterChange({ workTypes: next as MachineBookingSource[] });
  };

  // ── Count active filters for the badge ───────────────────────────────────
  const activeCount =
    (filterState.labSections.length > 0 ? 1 : 0) +
    (filterState.machineCodes.length > 0 ? 1 : 0) +
    (filterState.workTypes.length > 0 ? 1 : 0);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        flexWrap: 'wrap',
      }}
    >
      {/* Filter icon + label */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <FilterAlt
          fontSize="small"
          sx={{
            color: hasActiveFilters
              ? theme.palette.primary.main
              : theme.palette.text.secondary,
            fontSize: '1rem',
          }}
        />
        <Typography
          variant="caption"
          fontWeight={600}
          color={hasActiveFilters ? 'primary' : 'text.secondary'}
          sx={{ whiteSpace: 'nowrap', fontSize: '0.72rem' }}
        >
          Filters
          {activeCount > 0 && (
            <Chip
              label={activeCount}
              size="small"
              color="primary"
              sx={{
                ml: 0.5,
                height: 16,
                fontSize: '0.6rem',
                fontWeight: 700,
                '& .MuiChip-label': { px: 0.75 },
              }}
            />
          )}
        </Typography>
      </Box>

      {/* Lab Section dropdown */}
      <FormControl size="small" sx={{ minWidth: 148 }}>
        <InputLabel shrink sx={{ fontSize: '0.78rem' }}>
          Lab Section
        </InputLabel>
        <Select
          multiple
          value={filterState.labSections}
          onChange={handleLabSectionChange}
          label="Lab Section"
          displayEmpty
          {...MULTI_SELECT_PROPS}
          renderValue={(selected: string[]) =>
            selected.length === 0
              ? <Typography variant="caption" color="text.secondary">All Sections</Typography>
              : <Typography variant="caption">{selected.join(', ')}</Typography>
          }
          sx={{ fontSize: '0.78rem', '& .MuiSelect-select': { py: '5px' } }}
        >
          {LAB_SECTION_OPTIONS.map((section) => (
            <MenuItem key={section} value={section} dense>
              <Checkbox
                size="small"
                checked={filterState.labSections.includes(section)}
                sx={{ py: 0, pl: 0, pr: 0.5 }}
              />
              <ListItemText
                primary={section}
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Machine dropdown */}
      <FormControl size="small" sx={{ minWidth: 160 }}>
        <InputLabel shrink sx={{ fontSize: '0.78rem' }}>
          Machine
        </InputLabel>
        <Select
          multiple
          value={filterState.machineCodes}
          onChange={handleMachineChange}
          label="Machine"
          displayEmpty
          {...MULTI_SELECT_PROPS}
          renderValue={(selected: string[]) =>
            selected.length === 0
              ? <Typography variant="caption" color="text.secondary">All Machines</Typography>
              : <Typography variant="caption">{selected.join(', ')}</Typography>
          }
          sx={{ fontSize: '0.78rem', '& .MuiSelect-select': { py: '5px' } }}
        >
          {machineOptions.length === 0 && (
            <MenuItem disabled dense>
              <Typography variant="body2" color="text.secondary">
                No machines for selected section
              </Typography>
            </MenuItem>
          )}
          {machineOptions.map((m) => (
            <MenuItem key={m.machineKey} value={m.code} dense>
              <Checkbox
                size="small"
                checked={filterState.machineCodes.includes(m.code)}
                sx={{ py: 0, pl: 0, pr: 0.5 }}
              />
              <ListItemText
                primary={`${m.code} — ${m.name}`}
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Work Type dropdown */}
      <FormControl size="small" sx={{ minWidth: 148 }}>
        <InputLabel shrink sx={{ fontSize: '0.78rem' }}>
          Work Type
        </InputLabel>
        <Select
          multiple
          value={filterState.workTypes}
          onChange={handleWorkTypeChange}
          label="Work Type"
          displayEmpty
          {...MULTI_SELECT_PROPS}
          renderValue={(selected: string[]) =>
            selected.length === 0
              ? <Typography variant="caption" color="text.secondary">All Types</Typography>
              : (
                <Typography variant="caption">
                  {(selected as MachineBookingSource[]).map((s) => WORK_TYPE_LABELS[s]).join(', ')}
                </Typography>
              )
          }
          sx={{ fontSize: '0.78rem', '& .MuiSelect-select': { py: '5px' } }}
        >
          {WORK_TYPE_OPTIONS.map((src) => (
            <MenuItem key={src} value={src} dense>
              <Checkbox
                size="small"
                checked={filterState.workTypes.includes(src)}
                sx={{ py: 0, pl: 0, pr: 0.5 }}
              />
              <ListItemText
                primary={WORK_TYPE_LABELS[src]}
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Clear Filters */}
      {hasActiveFilters && (
        <Button
          size="small"
          variant="text"
          startIcon={<FilterAltOff fontSize="small" />}
          onClick={onClearFilters}
          sx={{
            textTransform: 'none',
            fontSize: '0.75rem',
            color: theme.palette.text.secondary,
            '&:hover': {
              color: theme.palette.primary.main,
              backgroundColor: 'transparent',
            },
            px: 0.75,
            minWidth: 'auto',
          }}
        >
          Clear Filters
        </Button>
      )}
    </Box>
  );
};
